/**
 * Global Feature Flag Manager
 * Manages a single PostHog listener for all feature flag subscriptions
 * Prevents memory leaks by centralizing callback management
 */

class FeatureFlagManager {
  static instance = null;

  listeners = new Map();

  posthogInstance = null;

  isListening = false;

  static getInstance() {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager();
    }
    return FeatureFlagManager.instance;
  }

  initialize(posthogService) {
    const posthog = posthogService.getPosthogInstance();

    if (!posthog || this.posthogInstance === posthog) {
      return;
    }

    this.posthogInstance = posthog;

    if (window.posthog && window.posthog !== posthog) {
      this.posthogInstance = window.posthog;
    }

    this.setupGlobalListener();
  }

  setupGlobalListener() {
    if (!this.posthogInstance || this.isListening) {
      return;
    }

    this.posthogInstance.onFeatureFlags(() => {
      this.listeners.forEach((listener) => {
        const enabled = this.posthogInstance.isFeatureEnabled(listener.flagKey) ?? false;
        const payload = this.posthogInstance.getFeatureFlagPayload(listener.flagKey);
        listener.callback(enabled, payload);
      });
    });

    this.isListening = true;
  }

  subscribe(flagKey, callback) {
    const id = Symbol('flag-listener');
    this.listeners.set(id, { flagKey, callback });

    if (this.listeners.size === 1 && !this.isListening && this.posthogInstance) {
      this.setupGlobalListener();
    }

    return () => {
      this.listeners.delete(id);
    };
  }

  getFeatureFlag(flagKey, defaultValue = false) {
    if (!this.posthogInstance) {
      return defaultValue;
    }
    return this.posthogInstance.isFeatureEnabled(flagKey) ?? defaultValue;
  }

  getFeatureFlagPayload(flagKey) {
    if (!this.posthogInstance) {
      return null;
    }
    return this.posthogInstance.getFeatureFlagPayload(flagKey);
  }

  reloadFeatureFlags() {
    if (this.posthogInstance) {
      this.posthogInstance.reloadFeatureFlags();
    }
  }

  clear() {
    this.listeners.clear();
  }
}

export const featureFlagManager = FeatureFlagManager.getInstance();
